// src/pages/BidPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/App.css';

// Bid data for different clients
const bidData = {
  'vetted-voices': {
    password: 'voiceactors',
    clientName: 'Tyson Kuteyi',
    projectTitle: 'Vetted Voice MVP Platform',
    preparedBy: 'Jake Handy',
    date: 'December 17, 2025',
    sections: [
      {
        title: '1. Executive Summary',
        content: `This proposal outlines the development of the Vetted Voice Minimum Viable Product (MVP). The platform will connect 100 vetted voice actors with paying clients, facilitating a complete "casting loop"—from script upload and AI auditions to secure booking and payment.`
      },
      {
        title: '2. Scope of Work',
        intro: 'The build is based on the High Level Requirements v0.1 and our subsequent discussions regarding scope adjustments.',
        subsections: [
          {
            subtitle: 'Core Modules',
            items: [
              {
                name: 'User Roles & Authentication',
                details: [
                  'Secure login for Talent, Clients, and Admins.',
                  'Jurisdiction support for North America, South America, and the UK.'
                ]
              },
              {
                name: 'Talent Profiles & Onboarding',
                details: [
                  'Profile creation (Headshot, Bio, Demos).',
                  'Union vs. Non-Union Logic: Selector/filter to distinguish workflows. Clients can browse/collect from either pool exclusively.',
                  'AI Clone Linkage: Integration with Supertone (or chosen provider) to link talent profiles with their AI voice models.'
                ]
              },
              {
                name: 'Project Creation & Fast Casting',
                details: [
                  'Script Parsing: Auto-extract text from PDF/DOCX for audition generation.',
                  'Casting Caps: Search results limited (e.g., batch of 50) to prevent client overwhelm.',
                  '"Spin the Wheel": Feature allowing clients to request an additional batch of talent.'
                ]
              },
              {
                name: 'Asset Management & Usage',
                details: [
                  'Usage Tracking: Clients input Campaign Start Dates and commercial codes for accurate tracking/renewals.'
                ]
              },
              {
                name: 'Booking & Commerce',
                details: [
                  'Stripe Integration: Handling of deposits, payments, and payouts.',
                  'Digital Agreements: Auto-generation of contracts with e-signatures.'
                ]
              }
            ]
          },
          {
            subtitle: 'Out of Scope (MVP)',
            items: [
              {
                name: 'Voice Fingerprinting',
                details: [
                  'Robust ACR/fingerprinting (e.g., Vobile) is excluded from this phase to expedite launch.'
                ]
              }
            ]
          }
        ]
      },
      {
        title: '3. Timeline & Methodology',
        content: 'Development will proceed at a steady pace of approximately 10 hours per week. This sets the total build timeline to roughly 4–5 months.',
        phases: [
          {
            name: 'Phase 1: Foundation',
            duration: 'Month 1',
            items: [
              'Architecture setup, Database design, User Auth.',
              'Basic Talent & Client Profile management.'
            ]
          },
          {
            name: 'Phase 2: The "Casting Loop"',
            duration: 'Months 2-3',
            items: [
              'Project creation w/ Script Parsing.',
              'AI Audition generation & "Fast Casting" logic.'
            ]
          },
          {
            name: 'Phase 3: Commerce & Legal',
            duration: 'Month 4',
            items: [
              'Stripe Connect integration.',
              'Dynamic Contract generation & Usage Tracking features.'
            ]
          },
          {
            name: 'Phase 4: Polish & Launch',
            duration: 'Month 5',
            items: [
              'QA Testing, UI Polish, Deployment.'
            ]
          }
        ]
      },
      {
        title: '4. Investment',
        pricing: {
          hourlyRate: '$300.00',
          estimatedEffort: '~160 Hours',
          totalCost: '$48,000',
          weeklyBurn: '~$3,000/week (capped at 10 hours)'
        },
        paymentSchedule: {
          intro: 'Given the extended timeline, billing will occur on a monthly basis to maintain steady progress without large upfront milestones.',
          items: [
            { label: 'Deposit', value: '$6,000', note: 'Covers first 20 hours / ~2 weeks' },
            { label: 'Monthly Invoicing', value: '~$12,000/month', note: 'Invoices sent at the end of each month for hours logged' }
          ]
        }
      },
      {
        title: '5. Estimated Operational Costs (Post-Launch)',
        content: 'In addition to the development costs, the platform will incur monthly infrastructure fees. These are paid directly to the service providers.',
        operationalCosts: [
          { service: 'Cloudflare Pages', purpose: 'Hosting & CDN', cost: '$0 - $20' },
          { service: 'Cloudflare D1', purpose: 'Database', cost: '$5 - $25' },
          { service: 'Stripe', purpose: 'Payment Processing', cost: '2.9% + $0.30/txn' },
          { service: 'Supertone API', purpose: 'AI Voice Generation', cost: 'Usage-based (TBD)' },
          { service: 'Email Service', purpose: 'Transactional Emails', cost: '$0 - $20' }
        ],
        note: 'Note: These costs will scale with your user base but are minimal for the MVP phase.'
      }
    ]
  }
};

// Password Gate Component
const PasswordGate = ({ onUnlock, clientName }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [isShaking, setIsShaking] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === bidData[clientName]?.password) {
      // Store in session storage for this specific bid
      sessionStorage.setItem(`bid-unlocked-${clientName}`, 'true');
      onUnlock();
    } else {
      setError(true);
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
    }
  };

  return (
    <div className="bid-password-gate">
      <div className={`bid-password-card ${isShaking ? 'shake' : ''}`}>
        <div className="bid-lock-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>
        <h2 className="bid-password-title">Protected Document</h2>
        <p className="bid-password-subtitle">Enter the password to view this proposal</p>
        <form onSubmit={handleSubmit} className="bid-password-form">
          <input
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError(false);
            }}
            placeholder="Enter password"
            className={`bid-password-input ${error ? 'error' : ''}`}
            autoFocus
          />
          <button type="submit" className="bid-password-submit">
            Unlock
          </button>
        </form>
        {error && <p className="bid-password-error">Incorrect password</p>}
      </div>
    </div>
  );
};

// Proposal Content Component
const ProposalContent = ({ data }) => {
  const [visibleSections, setVisibleSections] = useState(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    // Observe all sections
    document.querySelectorAll('.bid-section').forEach((section) => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="bid-proposal-container">
      {/* Header */}
      <header className="bid-header">
        <div className="bid-header-content">
          <p className="bid-label">Proposal</p>
          <h1 className="bid-title">{data.projectTitle}</h1>
          <div className="bid-meta">
            <div className="bid-meta-item">
              <span className="bid-meta-label">Prepared For</span>
              <span className="bid-meta-value">{data.clientName}</span>
            </div>
            <div className="bid-meta-divider" />
            <div className="bid-meta-item">
              <span className="bid-meta-label">Prepared By</span>
              <span className="bid-meta-value">{data.preparedBy}</span>
            </div>
            <div className="bid-meta-divider" />
            <div className="bid-meta-item">
              <span className="bid-meta-label">Date</span>
              <span className="bid-meta-value">{data.date}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="bid-main">
        {data.sections.map((section, index) => (
          <section
            key={index}
            id={`section-${index}`}
            className={`bid-section ${visibleSections.has(`section-${index}`) ? 'visible' : ''}`}
          >
            <h2 className="bid-section-title">{section.title}</h2>

            {/* Regular content */}
            {section.content && (
              <p className="bid-section-content">{section.content}</p>
            )}

            {/* Intro text */}
            {section.intro && (
              <p className="bid-section-intro">{section.intro}</p>
            )}

            {/* Subsections (for Scope of Work) */}
            {section.subsections && section.subsections.map((subsection, subIndex) => (
              <div key={subIndex} className="bid-subsection">
                <h3 className="bid-subsection-title">{subsection.subtitle}</h3>
                {subsection.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="bid-module">
                    <h4 className="bid-module-name">{item.name}</h4>
                    <ul className="bid-module-details">
                      {item.details.map((detail, detailIndex) => (
                        <li key={detailIndex}>{detail}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}

            {/* Phases (for Timeline) */}
            {section.phases && (
              <div className="bid-timeline">
                {section.phases.map((phase, phaseIndex) => (
                  <div key={phaseIndex} className="bid-phase">
                    <div className="bid-phase-marker">
                      <div className="bid-phase-dot" />
                      {phaseIndex < section.phases.length - 1 && <div className="bid-phase-line" />}
                    </div>
                    <div className="bid-phase-content">
                      <div className="bid-phase-header">
                        <h4 className="bid-phase-name">{phase.name}</h4>
                        <span className="bid-phase-duration">{phase.duration}</span>
                      </div>
                      <ul className="bid-phase-items">
                        {phase.items.map((item, itemIndex) => (
                          <li key={itemIndex}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pricing (for Investment) */}
            {section.pricing && (
              <div className="bid-pricing">
                <div className="bid-pricing-grid">
                  <div className="bid-pricing-item">
                    <span className="bid-pricing-label">Hourly Rate</span>
                    <span className="bid-pricing-value">{section.pricing.hourlyRate}</span>
                  </div>
                  <div className="bid-pricing-item">
                    <span className="bid-pricing-label">Estimated Effort</span>
                    <span className="bid-pricing-value">{section.pricing.estimatedEffort}</span>
                  </div>
                  <div className="bid-pricing-item highlight">
                    <span className="bid-pricing-label">Total Estimated Cost</span>
                    <span className="bid-pricing-value large">{section.pricing.totalCost}</span>
                    <span className="bid-pricing-subvalue">{section.pricing.weeklyBurn}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Schedule */}
            {section.paymentSchedule && (
              <div className="bid-payment-schedule">
                <h3 className="bid-subsection-title">Payment Schedule</h3>
                <p className="bid-section-intro">{section.paymentSchedule.intro}</p>
                <div className="bid-payment-items">
                  {section.paymentSchedule.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="bid-payment-item">
                      <div className="bid-payment-header">
                        <span className="bid-payment-label">{item.label}</span>
                        <span className="bid-payment-value">{item.value}</span>
                      </div>
                      <span className="bid-payment-note">{item.note}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Operational Costs Table */}
            {section.operationalCosts && (
              <div className="bid-costs-table-container">
                <table className="bid-costs-table">
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th>Purpose</th>
                      <th>Est. Monthly Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.operationalCosts.map((cost, costIndex) => (
                      <tr key={costIndex}>
                        <td>{cost.service}</td>
                        <td>{cost.purpose}</td>
                        <td>{cost.cost}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {section.note && <p className="bid-costs-note">{section.note}</p>}
              </div>
            )}
          </section>
        ))}
      </main>

      {/* Footer */}
      <footer className="bid-footer">
        <div className="bid-footer-content">
          <p className="bid-footer-text">
            Prepared with care by <strong>Handy Co</strong>
          </p>
        </div>
      </footer>
    </div>
  );
};

// Main BidPage Component
const BidPage = () => {
  const { clientName } = useParams();
  const navigate = useNavigate();
  const [isUnlocked, setIsUnlocked] = useState(false);

  // Check if bid exists and if already unlocked
  useEffect(() => {
    if (!bidData[clientName]) {
      navigate('/404', { replace: true });
      return;
    }

    // Check session storage for unlock status
    const unlocked = sessionStorage.getItem(`bid-unlocked-${clientName}`);
    if (unlocked === 'true') {
      setIsUnlocked(true);
    }
  }, [clientName, navigate]);

  // If bid doesn't exist, don't render anything (will redirect)
  if (!bidData[clientName]) {
    return null;
  }

  return (
    <div className="bid-page">
      {isUnlocked ? (
        <ProposalContent data={bidData[clientName]} />
      ) : (
        <PasswordGate clientName={clientName} onUnlock={() => setIsUnlocked(true)} />
      )}
    </div>
  );
};

export default BidPage;
